import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import type { User } from "firebase/auth";
import {
  getCurrentAuthUser,
  subscribeToAuthState,
} from "@/firebase/auth-state";
import { MEMBERSHIP_PATH } from "@/constants/membershipCopy";
import { buildAuthRouteWithReturn } from "@/utils/navigation/publicMaps";
import { useOptionalSubscription } from "@/components/User/SubscriptionContext";
import { useEventsFeature } from "@/contexts/EventsFeatureContext";
import { MountainRangeBackground } from "@/components/MountainRangeBackground/MountainRangeBackground";
import {
  getForecastMenuItems,
  isSiteNavSectionActive,
  type SiteNavSection,
} from "./siteNavItems";
import "./MobileMenu.css";

export const SITE_MOBILE_NAV_PANEL_ID = "site-mobile-nav-panel";

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const subscription = useOptionalSubscription();
  const isPremiumUser = subscription?.isPremiumUser ?? false;
  const { ready: eventsFeatureReady, forecastEnabled } = useEventsFeature();
  const [user, setUser] = useState<User | null>(() => getCurrentAuthUser());
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    const unsub = subscribeToAuthState((u) => setUser(u));
    return unsub;
  }, []);

  useEffect(() => {
    setAvatarError(false);
  }, [user?.photoURL]);

  const forecastItems = getForecastMenuItems(forecastEnabled);
  const itemClass = (section: SiteNavSection, exactPath?: string) =>
    isSiteNavSectionActive(pathname, section, exactPath)
      ? "mobile-menu-item mobile-menu-item--active"
      : "mobile-menu-item";

  const handleNav = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleLogin = () => {
    navigate(buildAuthRouteWithReturn("/login", pathname, location.search));
    onClose();
  };

  const handleSignUp = () => {
    navigate(buildAuthRouteWithReturn("/signup", pathname, location.search));
    onClose();
  };

  const handleUserProfile = () => {
    navigate("/profile");
    onClose();
  };

  const resolvedPhotoUrl = user?.photoURL || null;
  const showAvatarImage = Boolean(user && resolvedPhotoUrl && !avatarError);
  const namePart = (user?.displayName || "").trim();
  const initials = user
    ? namePart
      ? namePart
          .split(/\s+/)
          .map((s) => s[0])
          .join("")
          .slice(0, 2)
          .toUpperCase() || (user.email?.[0] ?? "?").toUpperCase()
      : (user.email?.[0] ?? "?").toUpperCase()
    : "";

  return (
    <>
      {isOpen ? (
        <div
          className="mobile-menu-backdrop"
          aria-hidden="true"
          onClick={onClose}
        />
      ) : null}
      <div
        id={SITE_MOBILE_NAV_PANEL_ID}
        className="mobile-menu-panel"
        role="region"
        aria-label="Site menu"
        hidden={!isOpen}
      >
      {isOpen ? (
        <div className="mobile-menu">
          <div className="mobile-menu-body">
            <nav className="mobile-menu-primary" aria-label="Main navigation">
              <button
                type="button"
                className={itemClass("map")}
                onClick={() => handleNav("/maps")}
              >
                Map
              </button>
              <button
                type="button"
                className={itemClass("field-guide")}
                onClick={() => handleNav("/field-guide")}
              >
                Field Guide
              </button>
              {eventsFeatureReady ? (
                <button
                  type="button"
                  className={itemClass("experiences")}
                  onClick={() => handleNav("/events")}
                >
                  Experiences
                </button>
              ) : null}
              <div className="mobile-menu-group" role="group" aria-labelledby="mobile-menu-forecasts">
                <span id="mobile-menu-forecasts" className="mobile-menu-group__label">
                  Forecasts
                </span>
                {forecastItems.map((item) => (
                  <button
                    key={item.path}
                    type="button"
                    className={`${itemClass(item.section, item.path)} mobile-menu-item--nested`}
                    onClick={() => handleNav(item.path)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </nav>

            <div className="mobile-menu-divider" role="separator" />

            <nav className="mobile-menu-secondary" aria-label="More">
              <button
                type="button"
                className={
                  pathname === MEMBERSHIP_PATH
                    ? "mobile-menu-item mobile-menu-item--secondary mobile-menu-item--active"
                    : "mobile-menu-item mobile-menu-item--secondary"
                }
                onClick={() => handleNav(MEMBERSHIP_PATH)}
              >
                Membership
              </button>
              <button
                type="button"
                className="mobile-menu-item mobile-menu-item--secondary"
                onClick={() => handleNav("/support")}
              >
                Help Center
              </button>
              <button
                type="button"
                className="mobile-menu-item mobile-menu-item--secondary"
                onClick={() => handleNav("/changelog")}
              >
                Changelog
              </button>
            </nav>
            <div className="mobile-menu-mountains" aria-hidden>
              <MountainRangeBackground />
            </div>
          </div>

          <div className="mobile-menu-user">
            {user ? (
              <button
                type="button"
                className="mobile-menu-user-card"
                onClick={handleUserProfile}
                aria-label="Open your profile"
              >
                {showAvatarImage ? (
                  <img
                    src={resolvedPhotoUrl || undefined}
                    alt=""
                    className={`mobile-menu-user-avatar${isPremiumUser ? " mobile-menu-user-avatar--member" : ""}`}
                    referrerPolicy="no-referrer"
                    onError={() => setAvatarError(true)}
                  />
                ) : (
                  <div
                    className={`mobile-menu-user-avatar mobile-menu-user-avatar--initials${isPremiumUser ? " mobile-menu-user-avatar--member" : ""}`}
                    aria-hidden
                  >
                    {initials}
                  </div>
                )}
                <span className="mobile-menu-user-text">
                  <span className="mobile-menu-user-name">
                    {user.displayName?.trim() || "Account"}
                  </span>
                  {user.email ? (
                    <span className="mobile-menu-user-email">{user.email}</span>
                  ) : null}
                </span>
                <ChevronRight
                  className="mobile-menu-user-chevron"
                  size={18}
                  strokeWidth={2}
                  aria-hidden
                />
              </button>
            ) : (
              <div className="mobile-menu-user-guest">
                <button
                  type="button"
                  className="mobile-menu-user-guest__btn"
                  onClick={handleLogin}
                >
                  Log in
                </button>
                <span className="mobile-menu-user-guest__sep" aria-hidden>
                  ·
                </span>
                <button
                  type="button"
                  className="mobile-menu-user-guest__btn mobile-menu-user-guest__btn--emphasis"
                  onClick={handleSignUp}
                >
                  Sign up
                </button>
              </div>
            )}
          </div>
        </div>
      ) : null}
      </div>
    </>
  );
};

export default MobileMenu;
